//------------------------------------------------------------------------
// Map
//
// Original NPCA map
// https://npca.maps.arcgis.com/apps/StoryMapBasic/index.html?appid=423e937f86774c9f9ac119127a0734a2
//
// Mapbox resources
// https://www.mapbox.com/help/gl-dds-map-tutorial/
// https://www.mapbox.com/help/mapbox-gl-js-expressions/
// https://www.mapbox.com/help/gl-dds-map-tutorial/
// https://mapbox.github.io/mapbox-gl-native/macos/0.4.0/data-driven-styling.html
//------------------------------------------------------------------------
import {csv, json} from "d3-fetch";
import filter from "lodash/filter";
import mapboxgl from "mapbox-gl";
mapboxgl.accessToken = window.ENV.mapboxToken;

// Map colors
const colors = {
  darkGreen: "#384534",
  orange: "#dc841f",
};

const sidebarBreakpoint = 768;

export default class Map {
  constructor(el) {
    var self = this;

    // Check is browser supports WebGL
    if (!mapboxgl.supported()) {
      console.warn("Mapbox: WebGL is not supported in this browser");
      el.querySelector(".Map-warning").style.display = "";
      return false;
    }

    this.activeParkId = null;// used to change marker style on click
    this.hoverParkId = null;// used to change marker style on hover
    this.sidebar = document.querySelector(".Map-sidebar");
    this.sidebarCloseBtn = this.sidebar.querySelector(".Map-sidebar-close");
    this.sidebarParkLink = this.sidebar.querySelector(".Map-sidebar-parkLink");
    this.sidebarParkName = this.sidebar.querySelector(".Map-sidebar-parkName");
    this.sidebarSpeciesCount = this.sidebar.querySelector(".Map-sidebar-species-count");
    this.sidebarSpeciesList = this.sidebar.querySelector(".Map-sidebar-list");
    this.mediaQueryList = window.matchMedia(`(min-width: ${sidebarBreakpoint}px)`);
    this.isDesktop = this.mediaQueryList.matches;
    this.parkData = null;

    // Listen for breakpoint change
    this.mediaQueryList.addListener(evt => {
      this.isDesktop = evt.matches;
    });

    // Create map
    this.map = new mapboxgl.Map({
      container: "map",
      style: window.ENV.mapboxStyleUrl,
      center: self.isDesktop ? [-139, 45] : [-115, 44],
      maxZoom: 16,
      minZoom: self.isDesktop ? 1.5 : 1,
      zoom: self.isDesktop ? 2.55 : 1.2
    });

    // Add data and layers to map
    this.map.on("load", () => {

      // Add zoom and rotation controls to the map.
      this.map.addControl(new mapboxgl.NavigationControl({
        "position": "top-left"
      }));

      // Fetch species data (array of objects)
      // We don’t really care when this loads since we are going to show map first.
      // If the data hasn’t loaded when a marker is clicked, we just won’t show the sidebar.
      csv(window.ENV.speciesData).then( (speciesData) => {
        /*
        {
          group: "Mammals",
          link: "https://ecos.fws.gov/ecp/species/6329",
          name: "Gray bat",
          name_scientific: "Myotis grisescens",
          park_abbr: "ABLI",
          park_name: "Abraham Lincoln Birthplace National Historical Park",
          status: "Endangered"
        }
        */

        // Sidebar close button event
        this.sidebarCloseBtn.addEventListener("click", () => {
          this.hideSidebar();
        });

        // Fetch park GeoJSON data and add species data
        // We have to use D3 so we can add unique feature IDs to GeoJSON data before adding it to the map.
        // Without doing that, we can’t get the point on click to change the styles.
        // Tried loading data via tileset but the IDs contained numerous duplicates, and would change at certain zoom levels.
        // https://github.com/mapbox/mapbox-gl-js/issues/6019
        // https://github.com/mapbox/mapbox-gl-js/issues/2716
        // https://www.mapbox.com/mapbox-gl-js/example/live-update-feature/
        // https://www.mapbox.com/mapbox-gl-js/example/hover-styles/

        json(`https://api.mapbox.com/datasets/v1/npca/${window.ENV.mapboxDataId}/features?access_token=${window.ENV.mapboxToken}`).then( (parkData) => {
          self.parkData = parkData;

          // Add IDs to features (use standard “for” loop for best performance)
          for (var i=0, len=parkData.features.length; i<len; i++) {
            let feature = parkData.features[i];
            feature.id = i;

            let species = filter(speciesData, ["park_abbr", feature.properties.park_abbr]);
            feature.properties.count = species.length;
            feature.properties.species = species;
            feature.properties.has_page = feature.properties.park_abbr.toLowerCase() in window.ENV.parks;
          }

          this.map.addSource("park-data", { type: "geojson", data: parkData });
          this.addLayers();
          this.mapEvents();
          this.checkUrlHash();
        });
      });

    });
  }

  addLayers() {
    // Add the parks circles
    this.map.addLayer({
      id: "parks",
      type: "circle",
      source: "park-data",
      // This is how to add data hosted on Mapbox (e.g. custom tileset)
      // source: {
      //   type: "vector",
      //   url: "mapbox://" + window.ENV.mapboxTilesId
      // },
      // "source-layer": "park-units",
      paint: {
        // Add conditional styles for the active park
        // https://www.mapbox.com/mapbox-gl-js/example/hover-styles/
        "circle-color": ["case",
          ["boolean", ["get", "has_page"], false],
          colors.orange,
          colors.darkGreen
        ],
        "circle-opacity": ["case",
          ["boolean", ["feature-state", "active"], false],
          0.8,
          ["case",
            ["boolean", ["get", "has_page"], false],
            0.3,
            0.2
          ]
        ],
        // This doesn’t seem to work
        // https://github.com/mapbox/mapbox-gl-js/issues/4081
        // "circle-opacity-transition": {
        //   delay: 0,
        //   duration: 500
        // },
        // The following code was generated in Mapbox Studio
        // https://www.mapbox.com/studio/styles/npca/cjkwzj7re01km2sq9ocvs4jkd/edit/
        // Note: Using Mapbox expressions didn’t provide enough control given the outliers in this data, so we opted for setting a minumum size at each zoom level.
        "circle-radius": ["interpolate",
          ["linear"],
          ["zoom"],
          0,
          ["max", 3, ["/", ["to-number", ["get", "count"]], 7]],
          1,
          ["max", 3, ["/", ["to-number", ["get", "count"]], 5]],
          2,
          ["max", 4, ["/", ["to-number", ["get", "count"]], 2]],
          3,
          ["max", 6, ["to-number", ["get", "count"]]],
          4,
          ["max", 10, ["*", ["to-number", ["get", "count"]], 1.25]],
          5,
          ["max", 12, ["*", ["to-number", ["get", "count"]], 1.5]],
          6,
          ["max", 14, ["*", ["to-number", ["get", "count"]], 1.75]],
          7,
          ["max", 16, ["*", ["to-number", ["get", "count"]], 2]],
          8,
          ["max", 18, ["*", ["to-number", ["get", "count"]], 2.25]],
          9,
          ["max", 20, ["*", ["to-number", ["get", "count"]], 2.5]]
        ]
      }
    });

    // Add small fixed-width dots at each park location
    this.map.addLayer({
      id: "dots",
      type: "circle",
      source: "park-data",
      paint: {
        "circle-color": ["case",
          ["boolean", ["get", "has_page"], false],
          colors.orange,
          colors.darkGreen
        ],
        "circle-opacity": 1,
        "circle-stroke-width": 0,
        "circle-radius": 3
      }
    });

    // Create a popup, but don"t add it to the map yet.
    // https://jsfiddle.net/rasagy/sy0j1nqj/
    this.popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false
    });
  }

  mapEvents() {
    var self = this;

    // DEV: Log zoom level
    // this.map.on("zoomend", (evt) => {
    //   console.log(this.map.getZoom());
    // });

    // Show tooltip on click
    this.map.on("click", "parks", (e) => {
      var feature = e.features[0];

      // Updates the cursor to a hand (interactivity)
      this.map.getCanvas().style.cursor = "pointer";

      // Update URL hash with park ID
      var parkAbbr = e.features[0].properties.park_abbr;

      if ( history.replaceState ) {
        history.replaceState(null, '', '#' + parkAbbr.toLowerCase());
      }
      else {
        window.location.hash = parkAbbr.toLowerCase();
      }

      // Center the map
      this.centerMap(feature);

      // Show tooltip
      this.showPopup(feature);

      // Show sidebar
      this.showSidebar(feature);
    });

    // Hide tooltip when click off of it
    this.map.on("click", (e) => {
      // DEV: Get the center coordinates on click (for map config purposes)
      // let bounds = this.map.getBounds();
      // // Average the NE and SW coordinates to get the midpoint
      // let lng = (bounds._ne.lng + bounds._sw.lng) / 2;
      // let lat = (bounds._ne.lat + bounds._sw.lat) / 2;
      // console.log([lng, lat], this.map.getZoom());

      // Check if click location overlaps a park
      var features = this.map.queryRenderedFeatures(e.point, {
        layers: ["parks"]
      });

      if (!features.length) {
        this.hidePopup();
      }
    });

    // Change mouse cursor to pointer on tooltip hover
    this.map.on("mouseenter", "parks", function(e) {
      self.map.getCanvas().style.cursor = "pointer";

      var feature = e.features[0];
      // Deactiveate hover state
      if (self.hoverParkId) {
        self.map.setFeatureState(
          {
            source: "park-data",
            id: self.hoverParkId
          },
          { hover: false }
        );
      }

      // Activate hover state
      self.hoverParkId = feature.id;
      self.map.setFeatureState(
        {
          source: "park-data",
          id: self.hoverParkId
        },
        { hover: true }
      );
    });

    // Change it back on mouseleave
    this.map.on("mouseleave", "parks", function(e) {
      self.map.getCanvas().style.cursor = "";

      // Deactiveate hover state
      if (self.hoverParkId) {
        self.map.setFeatureState(
          {
            source: "park-data",
            id: self.hoverParkId
          },
          { hover: false }
        );
      }

    });
  }

  checkUrlHash() {
    // Select park based on abbr in URL hash
    var currentPark = window.location.hash.replace("#", "");

    if (!currentPark) {
      return false;
    }

    // Use Lodash to find the current park in the source data
    // Note: Mapbox filters only work on the visible markers in the map
    //       https://stackoverflow.com/a/41688592
    var parkFeature = filter(this.parkData.features, [
      "properties",
      { park_abbr: currentPark.toUpperCase() }
    ]);

    if (parkFeature) {
      // Hide info panel
      document.querySelector(".Map-info-close").click();

      // Select park
      var feature = parkFeature[0];
      this.centerMap(feature);
      this.showPopup(feature);
      this.showSidebar(feature);
    }
  }

  centerMap(feature) {
    let minZoom = 4.3;
    let currentZoom = this.map.getZoom();

    // Only center and zoom if map is in desktop view and below the minimum zoom
    if (!this.isDesktop || currentZoom >= minZoom) {
      return false;
    }

    // Get park coordinates
    let coords = feature.geometry.coordinates;

    this.map.flyTo({
      center: [
        coords[0] - 8,// offset slightly to account for the sidebar
        coords[1]
      ],
      zoom: minZoom,
      speed: 0.8,
      // This can be any easing function: it takes a number between
      // 0 and 1 and returns another number between 0 and 1.
      easing: function(pos) {
          return (-0.5 * (Math.cos(Math.PI*pos) -1));
      }
    });
  }

  showPopup(feature) {
    // Show the popup at the coordinates with some data
    // var feature = e.features[0];

    // Deactiveate currently active park
    if (this.activeParkId) {
      // console.log("Deactivate:", this.activeParkId);
      this.map.setFeatureState(
        {
          source: "park-data",
          // sourceLayer: "park-units",// only needed when using vector tileset
          id: this.activeParkId
        },
        { active: false }
      );
    }

    // Activate new park
    this.activeParkId = feature.id;

    // console.log("Activate:", this.activeParkId);
    this.map.setFeatureState(
      {
        source: "park-data",
        // sourceLayer: "park-units",// only needed when using vector tileset
        id: this.activeParkId
      },
      { active: true }
    );

    // Use NPS links by default unless park has a landing page on the site
    let parkAbbr = feature.properties.park_abbr.toLowerCase();
    let parkUrl = `https://www.nps.gov/${parkAbbr}/index.htm`;

    if (feature.properties.has_page) {
      parkUrl = `/${window.ENV.parks[parkAbbr]}`;
    }

    this.popup.setLngLat(feature.geometry.coordinates)
      .setHTML(`
        <div id="tooltip" class="Map-tooltip f-montserrat" style="z-index: 10;">
          <p class="Map-tooltip-name"><a class="Map-tooltip-link${feature.properties.has_page ? " has-page" : ""}" href="${parkUrl}">${feature.properties.park_name}</a></p>
          <p class="Map-tooltip-number"><b>${feature.properties.count}</b> listed species</p>
        </div>`).addTo(this.map);
  }

  hidePopup() {
    this.map.getCanvas().style.cursor = "";
    this.popup.remove();
  }

  // Update sidebar content and show
  showSidebar(feature) {
    var parkData = feature.properties;

    if (!parkData.species) {
      return false;
    }

    // Use NPS links by default unless park has a landing page on the site
    let parkUrl = `https://www.nps.gov/${parkData.park_abbr.toLowerCase()}/index.htm`;

    if (parkData.has_page) {
      parkUrl = `/${window.ENV.parks[parkData.park_abbr.toLowerCase()]}`;
      this.sidebarParkLink.href = parkUrl;
      this.sidebarParkLink.classList.remove("u-hide");
    } else {
      this.sidebarParkLink.classList.add("u-hide");
    }

    this.sidebarParkName.innerHTML = `<a href="${parkUrl}">${parkData.park_name}</a>`;

    this.sidebarSpeciesCount.textContent = parkData.count;

    let speciesItems = "";

    // Species array is converted to a string by Mapbox when added to the map, so we have to eval() it
    let species = eval(parkData.species);

    // Generate species list HTML
    species.forEach( (d) => {
      speciesItems += `
        <li class="Map-sidebar-item">
          <p class="Map-sidebar-meta">
            <span class="Map-sidebar-status">${d.status}</span>
            <span class="Map-sidebar-group">${d.group}</span>
          </p>
          <p class="Map-sidebar-name">${d.link ? `<a class="Map-sidebar-link" href="${d.link}">${d.name}</a>` : ""}</p>
          <p class="Map-sidebar-scientific f-klinic">${d.name_scientific}</p>
        </li>`;
    });

    this.sidebarSpeciesList.innerHTML = speciesItems;

    // Show sidebar and scroll to top
    this.sidebar.setAttribute("aria-hidden", "false");
    this.sidebar.scrollTop = 0;
  }

  hideSidebar() {
    this.sidebar.setAttribute("aria-hidden", "true");
  }
}

let mapEl = document.querySelector("#map");

if (mapEl) {
  new Map(mapEl);
}
